import React, { useState } from "react";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";

/**
 * @template T
 * @param {object} props
 * @param {string} props.label - Label for the filter button
 * @param {T[]} props.options - Array of filter options
 * @param {T[]} props.defaultSelected - Array of filter options to be selected by default
 * @param {function(Set<T>): void} props.onFilterChange - Callback when filter selection changes
 * @param {function(T): string} props.getLabel - Function to get display label for each option
 * @param {function(T): string} props.getDescription - Function to get description for each option
 */
export const FilterButton = ({ label, options, defaultSelected, onFilterChange, getLabel, getDescription }) => {
  const [filterExpanded, setFilterExpanded] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState(() => new Set(defaultSelected));

  const handleToggleFilter = () => {
    setFilterExpanded(!filterExpanded);
  };

  /**
   * @param {T} option
   */
  const handleToggleOption = (option) => {
    setSelectedOptions((prev) => {
      const next = new Set(prev);
      if (next.has(option)) {
        next.delete(option);
      } else {
        next.add(option);
      }
      onFilterChange(next);
      return next;
    });
  };

  const buttonText =
    selectedOptions.size === 0 || selectedOptions.size === options.length
      ? label
      : `${label} (${selectedOptions.size})`;

  return (
    <Box p={1}>
      <Button
        variant="outlined"
        onClick={handleToggleFilter}
        endIcon={filterExpanded ? <ExpandLess /> : <ExpandMore />}
      >
        {buttonText}
      </Button>
      <Collapse in={filterExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 2 }}>
          <List component="div" disablePadding>
            {options.map((option) => (
              <ListItemButton key={String(option)} onClick={() => handleToggleOption(option)}>
                <ListItemIcon>
                  <Checkbox edge="start" checked={selectedOptions.has(option)} tabIndex={-1} disableRipple />
                </ListItemIcon>
                <ListItemText primary={getLabel(option)} secondary={getDescription(option)} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Collapse>
    </Box>
  );
};
